import React, { useCallback, useRef, useState } from 'react';

import { useOutsideClick } from '../hooks/CommonHooks.jsx';

import './SearchCombobox.scss';

/**
 * Type-ahead search box. The parent owns the query and the results, this handles the open state,
 * keyboard navigation (up/down/enter/escape) and showing the selected item's label.
 */
const SearchCombobox = ({
  id,
  label,
  placeholder,
  selectedText,
  query,
  onQueryChange,
  results,
  getResultKey,
  renderResult,
  onSelect,
  loading,
  error,
  emptyText,
  minLength,
  autoFocus,
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const close = useCallback(() => {
    setOpen(false);
    setEditing(false);
    onQueryChange('');
  }, [onQueryChange]);
  useOutsideClick(containerRef, close);

  const hasQuery = query.trim().length >= minLength;
  const showList = open && editing && hasQuery;

  const select = (item) => {
    onSelect(item);
    setOpen(false);
    setEditing(false);
    onQueryChange('');
  };

  const handleChange = (event) => {
    onQueryChange(event.target.value);
    setEditing(true);
    setOpen(true);
    setHighlighted(0);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlighted(index => Math.min(index + 1, Math.max(results.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && showList && results.length > 0) {
      event.preventDefault();
      select(results[Math.min(highlighted, results.length - 1)]);
    } else if (event.key === 'Escape') {
      close();
      event.target.blur();
    }
  };

  const renderList = () => {
    if (error) {
      return <li className={'combobox-message error'}>{error}</li>;
    }
    if (loading && results.length === 0) {
      return <li className={'combobox-message'}>{'Searching...'}</li>;
    }
    if (results.length === 0) {
      return <li className={'combobox-message'}>{emptyText}</li>;
    }
    return results.map((item, index) => (
      <li
        key={getResultKey(item)}
        role={'option'}
        aria-selected={index === Math.min(highlighted, results.length - 1)}
        className={`combobox-option${index === Math.min(highlighted, results.length - 1) ? ' highlighted' : ''}`}
        onMouseDown={(event) => { event.preventDefault(); select(item); }}
        onMouseEnter={() => setHighlighted(index)}
      >
        {renderResult(item)}
      </li>
    ));
  };

  return (
    <div className={'search-combobox'} ref={containerRef}>
      <label className={'combobox-label'} htmlFor={id}>{label}</label>
      <input
        id={id}
        className={'combobox-input'}
        type={'text'}
        autoComplete={'off'}
        role={'combobox'}
        aria-expanded={showList}
        aria-controls={`${id}-list`}
        placeholder={placeholder}
        value={editing ? query : (selectedText || query)}
        autoFocus={autoFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={(event) => { setOpen(true); event.target.select(); }}
      />
      { showList && <ul id={`${id}-list`} role={'listbox'} className={'combobox-list'}>{renderList()}</ul> }
    </div>
  );
};

SearchCombobox.defaultProps = {
  minLength: 1,
  results: [],
  emptyText: 'No matches',
};

export default SearchCombobox;
